import React, { useEffect, useState } from 'react';
import api, { asArray } from '../../services/api';

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get('/help/admin')
      .then(r => setThreads(asArray(r.data)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openThread = (t) => {
    setSelected(t);
    setReply('');
    setError('');
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setError('');
    if (!reply.trim()) {
      setError('Reply is required');
      return;
    }
    if (reply.trim().length > 200) {
      setError('Max 200 characters');
      return;
    }
    setSending(true);
    try {
      await api.post('/help/admin/reply', {
        threadId: selected.threadId,
        message: reply.trim(),
      });
      setReply('');
      const r = await api.get('/help/admin');
      const list = asArray(r.data);
      setThreads(list);
      const updated = list.find(x => x.threadId === selected.threadId);
      setSelected(updated || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reply');
    } finally {
      setSending(false);
    }
  };

  const deleteThread = async (threadId) => {
    if (!window.confirm('Delete this entire conversation?')) return;
    try {
      await api.delete(`/help/admin/thread/${threadId}`);
      setSelected(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/help/admin/message/${messageId}`);
      const r = await api.get('/help/admin');
      const list = asArray(r.data);
      setThreads(list);
      if (selected) {
        const updated = list.find(x => x.threadId === selected.threadId);
        setSelected(updated || null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  if (loading) return <p className="text-center text-slate-400 py-16">Loading messages…</p>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Messages</h1>
      <div className="grid lg:grid-cols-5 gap-4 min-h-[420px]">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-sm font-semibold text-slate-700">
            User messages ({threads.length})
          </div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-slate-50">
            {threads.length === 0 ? (
              <p className="p-4 text-sm text-slate-400">No messages yet.</p>
            ) : threads.map(t => (
              <button
                key={t.threadId}
                type="button"
                onClick={() => openThread(t)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${
                  selected?.threadId === t.threadId ? 'bg-slate-50' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-slate-900 truncate">
                    {t.userName || t.userEmail || `User #${t.userId}`}
                  </span>
                  {t.unread && (
                    <span className="text-[10px] font-bold uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">New</span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">ID {t.userId} · {t.userEmail}</p>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{t.preview}</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  {t.createdAt ? new Date(t.createdAt).toLocaleString() : ''}
                  {t.lastAt && t.lastAt !== t.createdAt && (
                    <> · last {new Date(t.lastAt).toLocaleString()}</>
                  )}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8">
              Select a message to read and reply
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-slate-900">{selected.userName || selected.userEmail}</p>
                  <p className="text-xs text-slate-400">
                    User ID {selected.userId} · {selected.userEmail}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => deleteThread(selected.threadId)}
                  className="text-xs bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-100 px-2.5 py-1 rounded-lg"
                >
                  Delete conversation
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[360px]">
                {(selected.messages || []).map(m => (
                  <div
                    key={m.id}
                    className={`rounded-xl px-3 py-2.5 text-sm border ${
                      m.senderRole === 'ADMIN'
                        ? 'bg-emerald-50 border-emerald-100 ml-6'
                        : 'bg-slate-50 border-slate-100 mr-6'
                    }`}
                  >
                    <div className="flex justify-between text-[11px] text-slate-400 mb-1 gap-2">
                      <span className="font-semibold text-slate-600">
                        {m.senderRole === 'ADMIN' ? 'Admin' : 'User'}
                      </span>
                      <span className="flex items-center gap-2">
                        <span>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</span>
                        <button
                          type="button"
                          onClick={() => deleteMessage(m.id)}
                          className="text-rose-600 hover:underline"
                        >
                          Delete
                        </button>
                      </span>
                    </div>
                    <p className="text-slate-800 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={sendReply} className="border-t border-slate-100 p-4">
                {error && <p className="text-xs text-rose-600 mb-2">{error}</p>}
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value.slice(0, 200))}
                  rows={3}
                  placeholder="Write a reply (max 200 characters)…"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-900/10"
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-slate-400">{reply.length}/200</span>
                  <button
                    type="submit"
                    disabled={sending}
                    className="bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
                  >
                    {sending ? 'Sending…' : 'Reply'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
